import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
    Search,
    Download,
    XCircle,
    Mail,
    Loader2,
} from 'lucide-react'
import { apiService } from '../config/api'
import CSVExportModal from '../components/CSVExportModal';
import SmartPagination, { PAGE_SIZE_OPTIONS } from '../components/SmartPagination';
import Select from 'react-select';

const DEFAULT_PAGE_SIZE = 50;

function ResultsSkeletonRows({ count = DEFAULT_PAGE_SIZE }) {
    return Array.from({ length: count }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
            <td className="px-6 py-4"><div className="h-4 w-6 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: `${120 + (i % 4) * 30}px` }} /></td>
            <td className="px-6 py-4 text-center"><div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse mx-auto" /></td>
            <td className="px-6 py-4"><div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4"><div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
            <td className="px-6 py-4 text-center"><div className="h-5 w-10 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse mx-auto" /></td>
            <td className="px-6 py-4"><div className="h-4 w-14 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
        </tr>
    ));
}

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'var(--rs-control-bg, white)',
        borderColor: state.isFocused ? '#6366f1' : 'var(--rs-control-border, #374151)',
        boxShadow: state.isFocused ? '0 0 0 2px #6366f133' : 'none',
        borderRadius: '0.5rem',
        minHeight: '2.5rem',
        fontSize: '1rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        color: 'var(--rs-control-text, #111827)'
    }),
    valueContainer: (base) => ({
        ...base,
        color: 'var(--rs-control-text, #111827)'
    }),
    menu: base => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        backgroundColor: 'var(--rs-menu-bg, white)',
        color: 'var(--rs-menu-text, #111827)'
    }),
    menuList: base => ({
        ...base,
        backgroundColor: 'var(--rs-menu-bg, white)',
        color: 'var(--rs-menu-text, #111827)'
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--rs-option-selected, #e5e7eb)'
            : state.isFocused
                ? 'var(--rs-option-hover, #f3f4f6)'
                : 'var(--rs-option-bg, white)',
        color: 'var(--rs-option-text, #111827)'
    }),
    singleValue: base => ({
        ...base,
        color: 'var(--rs-control-text, #111827)'
    }),
    placeholder: base => ({
        ...base,
        color: 'var(--rs-placeholder, #6b7280)'
    }),
    input: base => ({
        ...base,
        color: 'var(--rs-control-text, #111827)'
    }),
    dropdownIndicator: base => ({
        ...base,
        color: 'var(--rs-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    clearIndicator: base => ({
        ...base,
        color: 'var(--rs-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    indicatorSeparator: base => ({
        ...base,
        backgroundColor: 'var(--rs-control-border, #374151)'
    })
};

const ALL_STATUSES = [
    'Valid',
    'Invalid',
    'Catch All',
    'Unknown',
    'Disposable',
    'Role Account',
    'Free Account',
    'Skipped',
    'Syntax Error',
];

export default function Results() {
    const location = useLocation();
    const [results, setResults] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isPageLoading, setIsPageLoading] = useState(false)
    const [batchesLoading, setBatchesLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [nodeFilter, setNodeFilter] = useState('')
    const [availableNodes, setAvailableNodes] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(() =>
        Number(sessionStorage.getItem('resultsPageSize')) || DEFAULT_PAGE_SIZE
    )
    const [totalResults, setTotalResults] = useState(0)
    const [batchSummaries, setBatchSummaries] = useState([])
    const [selectedBatchId, setSelectedBatchId] = useState(null)
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');

    // Extract distinct groups client-side from batchSummaries
    const groups = [...new Set(batchSummaries.map(b => b.group_name).filter(Boolean))];
    const filteredBatches = batchSummaries.filter(b => selectedGroupFilter === 'all' || b.group_name === selectedGroupFilter);
    const currentBatch = batchSummaries.find(b => b.job_id === selectedBatchId);

    const [debouncedSearch, setDebouncedSearch] = useState('')
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400)
        return () => clearTimeout(t)
    }, [searchTerm])

    useEffect(() => {
        loadBatchSummaries()
    }, [])

    useEffect(() => {
        if (location.state && location.state.jobId && batchSummaries.length > 0) {
            const exists = batchSummaries.some(b => b.job_id === location.state.jobId);
            if (exists) {
                setSelectedBatchId(location.state.jobId);
            }
        }
    }, [batchSummaries, location.state]);

    useEffect(() => {
        if (selectedBatchId) {
            loadResults(selectedBatchId);
        }
    }, [selectedBatchId, currentPage, pageSize, statusFilter, nodeFilter, debouncedSearch])

    const loadBatchSummaries = async () => {
        try {
            setBatchesLoading(true);
            const response = await apiService.getJobs();
            const responseData = response.data.data || {};
            let jobs = Array.isArray(responseData) ? responseData : (responseData.jobs || []);
            setBatchSummaries(jobs);
            if (!selectedBatchId && jobs.length > 0) {
                setSelectedBatchId(jobs[0].job_id);
            }
        } catch (error) { } finally {
            setBatchesLoading(false);
        }
    };

    const loadResults = async (batchId) => {
        try {
            setIsLoading(true)
            setIsPageLoading(true)
            const params = {
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                status: statusFilter || undefined,
                search: debouncedSearch || undefined,
                node: nodeFilter || undefined
            }
            const response = await apiService.getJobItems(batchId, params)
            const items = response.data.data.items || [];
            setResults(items)
            setTotalResults(response.data.data.total || 0)
            // Collect unique node names for the dropdown
            const nodes = [...new Set(items.map(r => r.satellite_node_name).filter(Boolean))];
            setAvailableNodes(prev => {
                const merged = new Set([...prev, ...nodes]);
                return [...merged];
            });
        } catch (error) { } finally {
            setIsLoading(false)
            setIsPageLoading(false)
        }
    }

    // Derive extra column keys from the loaded results page (Apollo-style dynamic columns)
    const extraColumnKeys = useMemo(() => {
        const keySet = new Set();
        for (const r of results) {
            if (!r.extra_columns) continue;
            try {
                const extra = typeof r.extra_columns === 'string'
                    ? JSON.parse(r.extra_columns)
                    : r.extra_columns;
                if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
                    Object.keys(extra).forEach(k => {
                        // Skip internal/tag keys, keep readable column names
                        if (k !== 'tags' && k !== 'email') keySet.add(k);
                    });
                }
            } catch (_) {}
        }
        return [...keySet];
    }, [results]);

    const handleSearch = () => {
        setDebouncedSearch(searchTerm.trim())
        setCurrentPage(1)
    }

    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize) || 1)

    const handlePageSizeChange = (n) => {
        if (!PAGE_SIZE_OPTIONS.includes(n)) return;
        sessionStorage.setItem('resultsPageSize', String(n));
        setPageSize(n);
        setCurrentPage(1);
    }

    function getStatusBadgeColor(status) {
        switch (status) {
            case 'Valid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'Invalid': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'Catch All': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'Disposable': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            case 'Unknown': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            case 'Role Account': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'Free Account': return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300';
            case 'Skipped': return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
            case 'Syntax Error': return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800';
        }
    }

    function getSafeToSendLabel(status) {
        if (status === 'Valid') return 'Yes'
        if (status === 'Catch All') return 'Risky'
        return 'No'
    }

    function getSafeToSendBadgeColor(label) {
        switch (label) {
            case 'Yes': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'Risky': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            default: return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
        }
    }

    if (batchesLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <div className="relative">
                    <div className="h-16 w-16 rounded-full border-4 border-indigo-100 dark:border-indigo-900/40" />
                    <Loader2 className="h-16 w-16 animate-spin text-indigo-500 absolute inset-0" />
                </div>
                <p className="text-slate-500 dark:text-slate-400 font-medium animate-pulse">Loading your lists...</p>
            </div>
        );
    }

    if (!selectedBatchId) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <Mail className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <h2 className="text-xl font-bold">Select a list to view results</h2>
                    <div className="mt-4 w-64 mx-auto">
                        <Select
                            options={batchSummaries.map(batch => ({ value: batch.job_id, label: batch.job_name || batch.job_id }))}
                            onChange={option => setSelectedBatchId(option?.value)}
                            styles={customSelectStyles}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <CSVExportModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                selectedJob={batchSummaries.find(b => b.job_id === selectedBatchId)}
                selectedFilters={{ status: statusFilter, search: searchTerm, node: nodeFilter }}
            />
            <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold">List Results</h1>
                        {currentBatch?.group_name && (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2.5 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                                🏷️ {currentBatch.group_name}
                            </span>
                        )}
                    </div>
                    {currentBatch ? (
                        <p className="text-slate-500 text-sm">
                            Showing results for: <span className="font-bold text-slate-700 dark:text-slate-200">{currentBatch.job_name || selectedBatchId}</span>
                        </p>
                    ) : (
                        <p className="text-slate-500 text-sm">Review verification details for indexed lists.</p>
                    )}
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setExportModalOpen(true)} className="btn btn-primary" disabled={!selectedBatchId}><Download className="h-4 w-4 mr-2" />Export CSV</button>
                </div>
            </div>

            <div className="card">
                <div className="card-header flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
                    {/* Select List and Group filter side-by-side */}
                    <div className="flex flex-col md:flex-row gap-4 w-full lg:w-[600px] items-center">
                        <div className="w-full md:w-48">
                            <Select
                                options={[
                                    { value: 'all', label: '📁 All Groups' },
                                    ...groups.map(g => ({ value: g, label: `🏷️ ${g}` }))
                                ]}
                                value={selectedGroupFilter ? { value: selectedGroupFilter, label: selectedGroupFilter === 'all' ? '📁 All Groups' : `🏷️ ${selectedGroupFilter}` } : { value: 'all', label: '📁 All Groups' }}
                                onChange={option => {
                                    const nextGroup = option ? option.value : 'all';
                                    setSelectedGroupFilter(nextGroup);
                                    
                                    // Update selected list to first list of the new group
                                    const filtered = batchSummaries.filter(b => nextGroup === 'all' || b.group_name === nextGroup);
                                    if (filtered.length > 0) {
                                        setSelectedBatchId(filtered[0].job_id);
                                    } else {
                                        setSelectedBatchId(null);
                                    }
                                    setCurrentPage(1);
                                    setStatusFilter('');
                                    setTagFilter('');
                                    setSearchTerm('');
                                    setNodeFilter('');
                                }}
                                styles={customSelectStyles}
                                placeholder="Filter by group..."
                            />
                        </div>
                        <div className="flex-1 w-full">
                            <Select
                                options={filteredBatches.map(batch => ({ 
                                    value: batch.job_id, 
                                    label: batch.group_name ? `[${batch.group_name}] ${batch.job_name || batch.job_id}` : batch.job_name || batch.job_id 
                                }))}
                                value={selectedBatchId ? { 
                                    value: selectedBatchId, 
                                    label: batchSummaries.find(b => b.job_id === selectedBatchId)
                                        ? (batchSummaries.find(b => b.job_id === selectedBatchId).group_name 
                                            ? `[${batchSummaries.find(b => b.job_id === selectedBatchId).group_name}] ${batchSummaries.find(b => b.job_id === selectedBatchId).job_name || selectedBatchId}`
                                            : batchSummaries.find(b => b.job_id === selectedBatchId).job_name || selectedBatchId)
                                        : selectedBatchId 
                                } : null}
                                onChange={option => { 
                                    setSelectedBatchId(option?.value); 
                                    setCurrentPage(1); 
                                    setStatusFilter(''); 
                                    setTagFilter('');
                                    setSearchTerm(''); 
                                    setNodeFilter(''); 
                                }}
                                styles={customSelectStyles}
                                placeholder="Select a list..."
                                noOptionsMessage={() => "No lists in this category"}
                            />
                        </div>
                    </div>

                    {/* Filters row */}
                    <div className="flex flex-wrap gap-2 items-center lg:justify-end">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                className="input pl-9 h-10 w-48"
                                placeholder="Search email..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                        </div>

                        {/* Status filter */}
                        <select
                            className="input h-10 py-0 w-44"
                            value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">All Statuses</option>
                            {ALL_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>

                        {/* Node filter */}
                        {availableNodes.length > 0 && (
                            <select
                                className="input h-10 py-0 w-40"
                                value={nodeFilter}
                                onChange={e => { setNodeFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="">All Nodes</option>
                                {availableNodes.map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        )}

                        <button className="btn btn-secondary h-10 px-4" onClick={() => { setStatusFilter(''); setSearchTerm(''); setNodeFilter(''); setCurrentPage(1); }}>
                            <XCircle className="h-4 w-4 mr-1" />Clear
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50 text-left">
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">#</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center">Status</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Type</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Node</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center">Safe</th>
                                {extraColumnKeys.map(key => (
                                    <th key={key} className="px-6 py-4 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                                        {key.replace(/_/g, ' ')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isPageLoading ? (
                                <ResultsSkeletonRows count={pageSize} />
                            ) : results.length === 0 ? (
                                <tr>
                                    <td colSpan={6 + extraColumnKeys.length} className="p-20 text-center text-slate-400">
                                        <Mail className="h-8 w-8 mx-auto mb-3 opacity-30" />
                                        No results found in this list.
                                    </td>
                                </tr>
                            ) : results.map((result, idx) => {
                                const safeLabel = getSafeToSendLabel(result.status);

                                // Parse extra_columns JSON for dynamic Apollo-style columns
                                let extraData = {};
                                if (result.extra_columns) {
                                    try {
                                        extraData = typeof result.extra_columns === 'string'
                                            ? JSON.parse(result.extra_columns)
                                            : result.extra_columns;
                                        if (typeof extraData !== 'object' || Array.isArray(extraData)) extraData = {};
                                    } catch (_) {}
                                }

                                return (
                                    <tr key={result.id ?? `${result.email}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4 text-sm">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td className="px-6 py-4 text-sm font-semibold">{result.email}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${getStatusBadgeColor(result.status)}`}>{result.status}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{result.type || 'Standard'}</td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{result.satellite_node_name || 'Master'}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${getSafeToSendBadgeColor(safeLabel)}`}>{safeLabel}</span>
                                        </td>
                                        {extraColumnKeys.map(key => (
                                            <td key={key} className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-[180px] truncate" title={extraData[key] ?? ''}>
                                                {extraData[key] !== undefined && extraData[key] !== null && extraData[key] !== ''
                                                    ? String(extraData[key])
                                                    : <span className="text-slate-300 dark:text-slate-600">—</span>
                                                }
                                            </td>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    {!isPageLoading && results.length === 0 && false && <div className="p-20 text-center text-slate-400">No results found in this list.</div>}
                </div>

                <div className={`px-6 pb-6 transition-opacity duration-200 ${isPageLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    <SmartPagination
                        currentPage={Math.min(currentPage, totalPages)}
                        totalPages={totalPages}
                        totalRows={totalResults}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={handlePageSizeChange}
                        disabled={isPageLoading}
                    />
                </div>
            </div>
        </div>
    )
}
